import { supabase } from '../lib/supabase'

export interface WeeklyFeature {
  id: string
  venue_id: string
  sponsor_ad_id: string | null
  week_start: string   // ISO date (YYYY-MM-DD), always a Monday by convention
  headline: string | null
  is_active: boolean
  created_at?: string
}

/** ISO date for the Monday of the week containing `d`. */
export function mondayOf(d: Date): string {
  const copy = new Date(d)
  const day = copy.getDay()               // 0=Sun
  const diff = day === 0 ? -6 : 1 - day   // back to Monday
  copy.setDate(copy.getDate() + diff)
  return copy.toISOString().split('T')[0]
}

/**
 * The feature for the CURRENT week, or null. Auto-expires: a row only
 * renders during its own week_start..+6 window — a stale "of the Week"
 * from three weeks ago is structurally impossible.
 */
export async function getCurrentWeeklyFeature(): Promise<WeeklyFeature | null> {
  try {
    const todayISO = new Date().toISOString().split('T')[0]
    const { data, error } = await supabase
      .from('weekly_features')
      .select('*')
      .eq('is_active', true)
      .lte('week_start', todayISO)
      .order('week_start', { ascending: false })
      .limit(1)
    if (error || !data || data.length === 0) return null
    const f = data[0] as WeeklyFeature
    // Within 7 days of its week_start?
    const start = new Date(f.week_start + 'T00:00:00')
    const ageDays = (Date.now() - start.getTime()) / 86_400_000
    return ageDays >= 0 && ageDays < 7 ? f : null
  } catch {
    return null
  }
}

// ── Admin CRUD ──────────────────────────────────────────────────────────

export async function listWeeklyFeatures(): Promise<WeeklyFeature[]> {
  const { data, error } = await supabase
    .from('weekly_features')
    .select('*')
    .order('week_start', { ascending: false })
    .limit(20)
  if (error) throw new Error(error.message)
  return (data ?? []) as WeeklyFeature[]
}

export async function saveWeeklyFeature(f: Partial<WeeklyFeature>): Promise<void> {
  const row = {
    venue_id: f.venue_id,
    sponsor_ad_id: f.sponsor_ad_id || null,
    week_start: f.week_start,
    headline: f.headline?.trim() || null,
    is_active: f.is_active ?? true,
  }
  const q = f.id
    ? supabase.from('weekly_features').update(row).eq('id', f.id)
    : supabase.from('weekly_features').insert([row])
  const { error } = await q
  if (error) throw new Error(error.message)  // surfaces UNIQUE week violations
}

export async function deleteWeeklyFeature(id: string): Promise<void> {
  const { error } = await supabase.from('weekly_features').delete().eq('id', id)
  if (error) throw new Error(error.message)
}
